import { Wall } from './wall';
import { Stand } from './stand';
import { Point } from '../../core/models/point';
import { Instrument } from './instrument';

export class StandBase {
	localId: number;
	x: number;
	y: number;
	name: string = '';
	angle: number;
	width: number;
	depth: number;
	instrumentPoints: any;
	fontSize: number = 12;
	colorBorder: string = '#000000';
	fontColor: string = '#000000';
	backgroundColor: string = '#e0e0e0';

	static findIntersections(points: Array<Point>, returnObject: any): void {
		Object.values(returnObject).forEach((line: { begin: Point, end: Point, draw: boolean }) => {
			for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
				let point = StandBase.lineIntersect(line, { begin: points[i], end: points[j] });
				if (point != null && StandBase.distance(line.begin, line.end) > StandBase.distance(line.begin, point)) {
					line.end = point;
					line.draw = true;
				}
			}
		});
	}

	constructor(x: number, y: number, width?: number, depth?: number, angle?: number, name?: string, points?: any[]) {
		this.localId = Math.floor(Math.random() * Math.pow(10, 12));
		this.x = Math.round(x * 100) / 100;
		this.y = Math.round(y * 100) / 100;

		if (width != null) this.width = width;
		if (depth != null) this.depth = depth;
		if (name != null) this.name = name;
		if (points != null) this.instrumentPoints = points;
		if (angle != null) {
			this.angle = angle;
		} else {
			this.angle = 0;
		}
	}

	points() {
		this.width = this.instrumentPoints
			?
			this.instrumentPoints[this.instrumentPoints.length - 1].begin.x + this.instrumentPoints[this.instrumentPoints.length - 1].points[0].x
			:
			this.width;

		const points = [
			{ x: this.x + this.width, y: this.y },
			{ x: this.x, y: this.y },
			{ x: this.x, y: this.y + this.depth },
			{ x: this.x + this.width, y: this.y + this.depth }
		];

		return points.map(p => this.rotatePoint(p.x, p.y));
	}

	getInnerAngle() {
		return Math.round(Math.atan(this.depth / this.width) * 180 / Math.PI);
	}

	rotatePoint(x: number, y: number) {
		let rotationX = this.x,
			rotationY = this.y,
			angle = this.angle * Math.PI / 180;

		return {
			x: (x - rotationX) * Math.cos(angle) - (y - rotationY) * Math.sin(angle) + rotationX,
			y: (x - rotationX) * Math.sin(angle) + (y - rotationY) * Math.cos(angle) + rotationY
		};
	}

	getOffset(walls: Array<Wall>, stands: Array<Stand>, instruments: Array<Instrument>) {
		let points = this.points(),
			returnList = ['right', 'top', 'left', 'bottom'],
			returnObject = {};

		for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
			let p1 = points[i],
				p2 = points[j],
				x = (p1.x + p2.x) / 2,
				y = (p1.y + p2.y) / 2,
				angle = (this.angle - i * 90) * Math.PI / 180;

			returnObject[returnList[i]] = {
				begin: { x, y },
				end: {
					x: x + 200 * Math.cos(angle),
					y: y + 200 * Math.sin(angle)
				},
				draw: false
			};
		}

		walls.forEach((wall: Wall) => StandBase.findIntersections(wall.points(), returnObject));
		stands.forEach((stand: Stand) => StandBase.findIntersections(stand.points(), returnObject));
		instruments.forEach((instrument: Instrument) => StandBase.findIntersections(instrument.points(), returnObject));

		return returnObject;
	}

	drag(x: number, y: number) {
		this.x += x;
		this.y += y;
		this.x = Math.round(this.x * 100) / 100;
		this.y = Math.round(this.y * 100) / 100;
	}

	static distance(p1: Point, p2: Point): number {
		const a = p1.x - p2.x,
			b = p1.y - p2.y;

		return Math.sqrt(a * a + b * b);
	}

	static lineIntersect(line1, line2): Point {
		let x1 = Math.round(line1.begin.x * 1000) / 1000, y1 = Math.round(line1.begin.y * 1000) / 1000,
			x2 = Math.round(line1.end.x * 1000) / 1000, y2 = Math.round(line1.end.y * 1000) / 1000,
			x3 = Math.round(line2.begin.x * 1000) / 1000, y3 = Math.round(line2.begin.y * 1000) / 1000,
			x4 = Math.round(line2.end.x * 1000) / 1000, y4 = Math.round(line2.end.y * 1000) / 1000;

		let x = Math.round(((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)) * 1000) / 1000;
		let y = Math.round(((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)) * 1000) / 1000;

		if (isNaN(x) || isNaN(y)) {
			return null;
		} else {
			if (x1 >= x2) {
				if (!(x2 <= x && x <= x1)) return null;
			} else {
				if (!(x1 <= x && x <= x2)) return null;
			}

			if (y1 >= y2) {
				if (!(y2 <= y && y <= y1)) return null;
			} else {
				if (!(y1 <= y && y <= y2)) return null;
			}

			if (x3 >= x4) {
				if (!(x4 <= x && x <= x3)) return null;
			} else {
				if (!(x3 <= x && x <= x4)) return null;
			}

			if (y3 >= y4) {
				if (!(y4 <= y && y <= y3)) return null;
			} else {
				if (!(y3 <= y && y <= y4)) return null;
			}
		}
		return { x, y };
	}
}